import { createRouter, createWebHistory } from 'vue-router'
import { useAuth, authInitialized } from '@/composables/useAuth'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('@/pages/LoginPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('@/pages/ForgotPasswordPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/pages/ResetPasswordPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('@/pages/VerifyEmailPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/',
      name: 'home',
      redirect: '/dashboard',
    },
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('@/pages/DashboardPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/inbox',
      name: 'inbox',
      component: () => import('@/pages/InboxPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/vulnerabilities',
      name: 'vulnerabilities',
      component: () => import('@/pages/VulnerabilitiesPageDataTable.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/assets',
      name: 'assets',
      component: () => import('@/pages/AssetsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/assets/:id',
      name: 'asset-detail',
      component: () => import('@/pages/AssetDetailPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/vulnerabilities/:qid',
      name: 'vulnerability-detail',
      component: () => import('@/pages/VulnerabilityDetailPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/settings',
      name: 'settings',
      component: () => import('@/pages/SettingsPage.vue'),
      meta: { requiresAuth: true },
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: () => import('@/pages/admin/ManageUsersPage.vue'),
      meta: { requiresAuth: true, requiresManageUsers: true },
    },
    {
      path: '/changelog',
      name: 'changelog',
      component: () => import('@/pages/ChangelogPage.vue'),
      meta: { requiresAuth: true },
    },
    // Super-admin console. Lazily loaded like every other route, so a tenant
    // user never downloads it. `requiresSuperAdmin` is a UX gate only - every
    // callable behind these screens re-checks the role server-side (see
    // functions/src/admin/guard.ts), which is the actual control.
    //
    // Platform-wide accounts live at /admin/accounts, NOT /admin/users: that
    // path belongs to the org_admin "Manage users" page above (GitHub #24),
    // which is scoped to user-role accounts within the caller's own tags.
    {
      path: '/admin',
      redirect: '/admin/tags',
    },
    {
      path: '/admin/tags',
      name: 'admin-tags',
      component: () => import('@/pages/admin/AdminTagsPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/admin/tags/:id',
      name: 'admin-tag-detail',
      component: () => import('@/pages/admin/AdminTagDetailPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/admin/accounts',
      name: 'admin-accounts',
      component: () => import('@/pages/admin/AdminUsersPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/admin/audit',
      name: 'admin-audit',
      component: () => import('@/pages/admin/AdminAuditLogPage.vue'),
      meta: { requiresAuth: true, requiresSuperAdmin: true },
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('@/pages/UnauthorizedPage.vue'),
      meta: { requiresAuth: false },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/NotFoundPage.vue'),
      meta: { requiresAuth: false },
    },
  ],
})

// Auth guard
router.beforeEach(async (to, _from, next) => {
  // Wait for Firebase to restore session (critical for page refresh)
  await authInitialized

  const { isAuthenticated } = useAuth()

  // Redirect to login if not authenticated
  if (to.meta.requiresAuth && !isAuthenticated.value) {
    return next('/login')
  }

  // Redirect away from login if already authenticated
  if (to.path === '/login' && isAuthenticated.value) {
    return next('/dashboard')
  }

  if (to.meta.requiresManageUsers) {
    const authStore = useAuthStore()
    // fetchUserRole() is triggered by a watcher on login, but a hard refresh
    // landing directly on this route can arrive before that watcher has
    // resolved - wait for it explicitly rather than racing an empty role.
    if (!authStore.userRole) {
      await authStore.fetchUserRole()
    }
    if (!authStore.canManageUsers) {
      return next('/unauthorized')
    }
  }

  if (to.meta.requiresSuperAdmin) {
    const authStore = useAuthStore()

    // Same race as above: a direct hit on /admin (or a refresh) can arrive
    // before the role has landed. Without this wait the guard would bounce a
    // genuine super admin to /unauthorized on every page refresh.
    if (!authStore.userRole) {
      await authStore.fetchUserRole()
    }

    if (!authStore.canUseAdminConsole) {
      return next('/unauthorized')
    }
  }

  next()
})

export default router
